// src/components/CarsSection.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 26.04.2025 to fix IMAGE_BASE_URL to use correct domain
// Updated by Grok on 27.04.2025 to ensure correct IMAGE_BASE_URL and add logging

import { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import SectionTitle from '../common/SectionTitle';
import { get } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const CarsSection = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);
  const language = getUserLanguage();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const extractYearFromSpecs = (car) => {
    const specData = car.translations?.[language] || car.translations?.en || {};
    const yearText = specData.specs_4 || '';
    const yearMatch = yearText.match(/\b(19|20)\d{2}\b/);
    return yearMatch ? parseInt(yearMatch[0]) : 0;
  };

  useEffect(() => {
    const fetchCars = async () => {
      try {
        console.log('[CarsSection] Fetching cars with language:', language);
        const headers = { 'accept-language': language };
        const data = await get('/cars', headers);
        console.log('[CarsSection] Cars fetched:', data.length);
        const activeCars = data.filter((car) => car.is_active);

        const sortedCars = [...activeCars].sort((a, b) => {
          const yearA = extractYearFromSpecs(a);
          const yearB = extractYearFromSpecs(b);
          return yearB - yearA;
        });

        const currentCarIndex = sortedCars.findIndex((car) => car.is_current);
        if (currentCarIndex >= 0) {
          setActiveSlide(currentCarIndex);
        }

        setCars(sortedCars);
      } catch (error) {
        console.error('[CarsSection] Error fetching cars:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst data o autech' : 'Failed to load cars data');
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, [language]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % cars.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + cars.length) % cars.length);
  };

  const goToSlide = (index) => {
    setActiveSlide(index);
  };

  useEffect(() => {
    let intervalId;
    const currentCarIndex = cars.findIndex((car) => car.is_current);

    if (
      inView &&
      cars.length > 1 &&
      (currentCarIndex === -1 || activeSlide !== currentCarIndex)
    ) {
      intervalId = setInterval(() => {
        nextSlide();
      }, 7000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeSlide, inView, cars]);

  if (loading) {
    return (
      <section id="cars" className="py-10 md:py-20 bg-dark-gray relative">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Naše auta' : 'Our Cars'}
            title={language === 'cs' ? 'Drift' : 'Drift'}
            highlightedText={language === 'cs' ? 'speciály' : 'specials'}
            description={language === 'cs' ? 'Načítání...' : 'Loading...'}
          />
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="cars" className="py-10 md:py-20 bg-dark-gray relative">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Naše auta' : 'Our Cars'}
            title={language === 'cs' ? 'Drift' : 'Drift'}
            highlightedText={language === 'cs' ? 'speciály' : 'specials'}
            description={error}
          />
        </div>
      </section>
    );
  }

  const descriptions = {
    cs: 'Představujeme naše závodní a exhibiční speciály, které jsou upraveny pro maximální výkon a přesnou kontrolu ve smyku.',
    en: 'Introducing our racing and exhibition specials, modified for maximum performance and precise drift control.',
  };

  const renderCarCard = (car, position) => {
    const carData =
      car.translations?.[language] ||
      car.translations?.en || {
        name: '',
        description: '',
        specs_1: '',
        specs_2: '',
        specs_3: '',
        specs_4: '',
      };
    const specLabels = {
      spec_1:
        car.spec_icons?.spec_1_label?.[language] ||
        (language === 'cs' ? 'Výkon' : 'Power'),
      spec_2:
        car.spec_icons?.spec_2_label?.[language] ||
        (language === 'cs' ? 'Motor' : 'Engine'),
      spec_3:
        car.spec_icons?.spec_3_label?.[language] ||
        (language === 'cs' ? 'Jezdec' : 'Driver'),
      spec_4:
        car.spec_icons?.spec_4_label?.[language] ||
        (language === 'cs' ? 'Sezóna' : 'Season'),
    };
    const specIcons = {
      spec_1: car.spec_icons?.spec_1_icon || 'fa-tachometer-alt',
      spec_2: car.spec_icons?.spec_2_icon || 'fa-cog',
      spec_3: car.spec_icons?.spec_3_icon || 'fa-user',
      spec_4: car.spec_icons?.spec_4_icon || 'fa-calendar',
    };

    const getSpecValue = (spec) => {
      return (
        carData[spec] ||
        (spec === 'specs_1' && car.horsepower
          ? `${car.horsepower} ${language === 'cs' ? 'koní' : 'hp'}`
          : language === 'cs'
          ? 'Není uvedeno'
          : 'Not specified')
      );
    };

    return (
      <div
        className={`drift-card group smoke-effect w-full mx-auto ${
          inView ? 'animate-fade-in' : 'opacity-0'
        } ${
          car.is_current
            ? 'current-car border-2 border-red-500 shadow-lg shadow-red-500/30'
            : ''
        }`}
        style={{
          animationDelay: `${0.2 * (position === 'center' ? 1 : position === 'left' ? 0 : 2)}s`,
          opacity: position === 'center' ? 1 : 0.6,
          transform: position === 'center' ? 'scale(1)' : 'scale(0.9)',
          transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
          zIndex: position === 'center' ? 10 : 5,
        }}
      >
        <div className="absolute top-4 right-4 z-10">
          {car.is_current ? (
            <span className="bg-red-500 text-white text-xs px-2 md:px-3 py-1 rounded-full font-semibold">
              {language === 'cs' ? 'Aktuální sezóna' : 'Current Season'}
            </span>
          ) : (
            <span className="bg-gray-700 text-white text-xs px-2 md:px-3 py-1 rounded-full font-semibold">
              {language === 'cs' ? 'Minulá sezóna' : 'Past Season'}
            </span>
          )}
        </div>
        <Link to={`/car/${car.slug}`} className="block">
          <div className="relative h-48 md:h-56 overflow-hidden">
            <img
              src={
                car.image_path
                  ? (import.meta.env.DEV ? car.image_path : `${IMAGE_BASE_URL}${car.image_path}`)
                  : '/images/cars/placeholder.jpeg'
              }
              alt={carData.name || 'Car'}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                console.error('[CarsSection] Image load error for car:', car.id, car.image_path);
                e.target.onerror = null;
                e.target.src = '/images/cars/placeholder.jpeg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
          </div>
        </Link>
        <div className="p-4 md:p-6">
          <h3 className="text-lg md:text-xl font-title font-bold text-white mb-2 group-hover:text-red-500 transition-colors">
            {carData.name ||
              (language === 'cs' ? 'Bezejmenné auto' : 'Unnamed Car')}
          </h3>
          <div className="gradient-line mb-3 md:mb-4"></div>
          <ul className="text-gray-400 text-xs md:text-sm space-y-2">
            <li className="flex items-center">
              <i className={`fas ${specIcons.spec_1} text-red-500 mr-2`}></i>
              <div>
                <span className="text-gray-300 font-medium">
                  {specLabels.spec_1}:
                </span>
                <span className="ml-1">{getSpecValue('specs_1')}</span>
              </div>
            </li>
            <li className="flex items-center">
              <i className={`fas ${specIcons.spec_2} text-red-500 mr-2`}></i>
              <div>
                <span className="text-gray-300 font-medium">
                  {specLabels.spec_2}:
                </span>
                <span className="ml-1">{getSpecValue('specs_2')}</span>
              </div>
            </li>
            <li className="flex items-center">
              <i className={`fas ${specIcons.spec_3} text-red-500 mr-2`}></i>
              <div>
                <span className="text-gray-300 font-medium">
                  {specLabels.spec_3}:
                </span>
                <span className="ml-1">{getSpecValue('specs_3')}</span>
              </div>
            </li>
            <li className="flex items-center">
              <i className={`fas ${specIcons.spec_4} text-red-500 mr-2`}></i>
              <div>
                <span className="text-gray-300 font-medium">
                  {specLabels.spec_4}:
                </span>
                <span className="ml-1">{getSpecValue('specs_4')}</span>
              </div>
            </li>
          </ul>
          <Link
            to={`/car/${car.slug}`}
            className="inline-flex items-center mt-3 md:mt-4 text-xs md:text-sm text-white hover:text-red-500 transition-colors"
          >
            <span>
              {language === 'cs'
                ? 'Technické specifikace'
                : 'Technical Specifications'}
            </span>
            <i className="fas fa-arrow-right ml-2 text-xs"></i>
          </Link>
        </div>
      </div>
    );
  };

  const getVisibleCars = () => {
    if (cars.length === 0) return [];

    const isMobile = window.innerWidth < 768;

    if (cars.length === 1 || isMobile) {
      return [{ car: cars[activeSlide], position: 'center' }];
    }

    if (cars.length <= 3) {
      return cars.map((car, index) => {
        let position = 'center';
        if (cars.length === 3) {
          position = index === 0 ? 'left' : index === 1 ? 'center' : 'right';
        } else if (cars.length === 2) {
          position = index === 0 ? 'left' : 'right';
        }
        return { car, position };
      });
    }

    const centerCar = cars[activeSlide];
    const leftIndex = (activeSlide - 1 + cars.length) % cars.length;
    const leftCar = cars[leftIndex];
    const rightIndex = (activeSlide + 1) % cars.length;
    const rightCar = cars[rightIndex];

    return [
      { car: leftCar, position: 'left' },
      { car: centerCar, position: 'center' },
      { car: rightCar, position: 'right' }
    ];
  };

  return (
    <section id="cars" className="py-10 md:py-20 bg-dark-gray relative" ref={sectionRef}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-red-600/5 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-red-600/5 blur-3xl rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionTitle
          badge={language === 'cs' ? 'Naše auta' : 'Our Cars'}
          title={language === 'cs' ? 'Drift' : 'Drift'}
          highlightedText={language === 'cs' ? 'speciály' : 'specials'}
          description={descriptions[language] || descriptions.en}
        />

        {cars.length === 0 ? (
          <div className="text-center text-gray-400 py-10">
            {language === 'cs'
              ? 'Žádná auta k zobrazení'
              : 'No cars to display'}
          </div>
        ) : (
          <div className="relative mt-8 md:mt-12 mb-12 md:mb-16 px-8 md:px-16" ref={sliderRef}>
            <div className="flex justify-center items-center">
              <div className="w-full max-w-6xl relative">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  {getVisibleCars().map(({ car, position }, index) => (
                    <div
                      key={car.id}
                      className={`${position === 'center' ? 'col-span-1' : 'hidden md:block col-span-1'}`}
                    >
                      <div className="w-full max-w-sm mx-auto">
                        {renderCarCard(car, position)}
                      </div>
                    </div>
                  ))}
                </div>

                {cars.length > 1 && (
                  <>
                    <button
                      className="absolute left-0 md:-left-6 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-red-500 text-white p-3 rounded-full z-40 transition-colors duration-300 shadow-md"
                      onClick={prevSlide}
                      aria-label={language === 'cs' ? 'Předchozí auto' : 'Previous car'}
                    >
                      <i className="fas fa-chevron-left text-sm md:text-base"></i>
                    </button>
                    <button
                      className="absolute right-0 md:-right-6 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-red-500 text-white p-3 rounded-full z-40 transition-colors duration-300 shadow-md"
                      onClick={nextSlide}
                      aria-label={language === 'cs' ? 'Další auto' : 'Next car'}
                    >
                      <i className="fas fa-chevron-right text-sm md:text-base"></i>
                    </button>
                  </>
                )}
              </div>
            </div>

            {cars.length > 1 && (
              <div className="flex justify-center mt-6 md:mt-8 gap-1 md:gap-2">
                {cars.map((_, index) => (
                  <button
                    key={index}
                    className={`h-2 md:h-3 rounded-full transition-all duration-300 ${
                      activeSlide === index
                        ? 'bg-red-500 w-6 md:w-8'
                        : 'bg-gray-600 w-2 md:w-3 hover:bg-gray-400'
                    }`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Přejít na slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-8 md:mt-12">
          <Link to="/cars-gallery" className="btn-drift text-sm md:text-base">
            <i className="fas fa-images text-xs mr-2"></i>
            {language === 'cs' ? 'Prohlédnout galerii' : 'View Gallery'}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CarsSection;